import authService from './auth.service';
import type {
  EntityType,
  EntityDocumentsResponse,
  EntityDocumentsParams,
  EntityDocument,
} from '../types/entity-document.types';

const API_URL = '/api/v1';

export interface UploadDocumentParams {
  entityType: EntityType;
  entityId: number;
  files: File | File[];
  documentTypeId?: number;
  description?: string;
  tags?: string;
}

class EntityDocumentsService {
  /**
   * Загрузка документов для сущности
   * POST /api/v1/entity-documents/upload
   * Принимает один или несколько файлов, всегда возвращает массив
   */
  async uploadDocuments(params: UploadDocumentParams): Promise<EntityDocument[]> {
    const { entityType, entityId, files, documentTypeId, description, tags } = params;

    const usp = new URLSearchParams();
    usp.set('entity_type', entityType);
    usp.set('entity_id', String(entityId));
    if (documentTypeId) {
      usp.set('document_type_id', String(documentTypeId));
    }
    if (description) {
      usp.set('description', description);
    }
    if (tags) {
      usp.set('tags', tags);
    }

    const url = `${API_URL}/entity-documents/upload?${usp.toString()}`;

    const formData = new FormData();
    const fileList = Array.isArray(files) ? files : [files];
    fileList.forEach((f) => formData.append('files', f, f.name));

    const res = await authService.fetchWithAuthForm(url, {
      method: 'POST',
      body: formData,
    });

    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить документ');
    }

    return (await res.json()) as EntityDocument[];
  }

  async getDocumentsByEntity(
    entityType: EntityType,
    entityId: number,
    params?: EntityDocumentsParams
  ): Promise<EntityDocumentsResponse> {
    const usp = new URLSearchParams();

    if (params?.skip !== undefined) {
      usp.set('skip', String(params.skip));
    }
    if (params?.limit !== undefined) {
      usp.set('limit', String(params.limit));
    }
    if (params?.origin) {
      usp.set('origin', params.origin);
    }
    if (params?.tags && params.tags.length > 0) {
      params.tags.forEach((tag) => usp.append('tags', tag));
    }

    const query = usp.toString();
    const url = `${API_URL}/entity-documents/by-entity/${entityType}/${entityId}${query ? `?${query}` : ''}`;

    const res = await authService.fetchWithAuth(url);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить документы');
    }

    return (await res.json()) as EntityDocumentsResponse;
  }

  /**
   * Удаление документа
   * DELETE /api/v1/entity-documents/{document_id}
   */
  async deleteDocument(documentId: number): Promise<void> {
    const url = `${API_URL}/entity-documents/${documentId}`;

    const res = await authService.fetchWithAuth(url, {
      method: 'DELETE',
    });

    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось удалить документ');
    }
  }

  /**
   * Обновление типа документа
   * PATCH /api/v1/entity-documents/{document_id}?document_type_id={type_id}
   */
  async updateDocumentType(documentId: number, documentTypeId: number | null): Promise<EntityDocument> {
    const usp = new URLSearchParams();
    if (documentTypeId !== null) {
      usp.set('document_type_id', String(documentTypeId));
    }

    const query = usp.toString();
    const url = `${API_URL}/entity-documents/${documentId}${query ? `?${query}` : ''}`;

    const res = await authService.fetchWithAuth(url, {
      method: 'PATCH',
    });

    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось обновить тип документа');
    }

    return (await res.json()) as EntityDocument;
  }
}

export default new EntityDocumentsService();
